import type { EventType, MedicalEvent, MedIngredient, MedUnit } from "@medassist/shared";
import { symptomFollowUpKey, formatMedStrength } from "@medassist/shared";
import { eventTypeLabel } from "../components/eventMeta.js";

export interface ItemIdentity {
  key: string;
  label: string;
  eventType: EventType;
  /** Event types to listEvents() for (D-IR-8: meds fetch both taken + as-needed). */
  fetchTypes: EventType[];
  /** Set only for medication items: the strength of the dose that opened the
   *  modal, used by the "By name+strength" granularity toggle (D-IR-9). */
  medStrength?: string;
  matches: (e: MedicalEvent) => boolean;
}

const MED_EVENT_TYPES: EventType[] = ["medication_taken", "as_needed_med"];

/**
 * Build a medication ItemIdentity directly from a name (+ optional dose
 * shape), without requiring a logged event. D-IR-8: folds `as_needed_med`
 * into `medication_taken` of the same name (key drops the event type).
 */
export function itemForMedication(
  name: string,
  dose?: { dose?: number; unit?: MedUnit; ingredients?: MedIngredient[] }
): ItemIdentity {
  const nameLower = name.toLowerCase();
  return {
    key: `medication::${nameLower}`,
    label: name || "Medication",
    eventType: "medication_taken",
    fetchTypes: MED_EVENT_TYPES,
    medStrength: dose ? formatMedStrength(dose) || undefined : undefined,
    matches: (ev) =>
      MED_EVENT_TYPES.includes(ev.eventType) &&
      ((ev.payload as Record<string, unknown>).name as string | undefined)?.toLowerCase() === nameLower,
  };
}

// D-IR-2 allowlist
const REPORTABLE_TYPES = new Set<EventType>([
  "medication_taken",
  "as_needed_med",
  "symptom",
  "headache",
  "mood",
  "sleep",
  "vital_sign",
  "stress_event",
  "exercise",
  "hydration",
  "bowel_movement",
  "food",
]);

export function isReportableType(t: EventType): boolean {
  return REPORTABLE_TYPES.has(t);
}

// D-IR-1: item scope
export function itemFromEvent(e: MedicalEvent): ItemIdentity {
  const p = e.payload as Record<string, unknown>;

  if (e.eventType === "medication_taken" || e.eventType === "as_needed_med") {
    const name = typeof p.name === "string" ? p.name : "";
    return itemForMedication(name, p as { dose?: number; unit?: MedUnit; ingredients?: MedIngredient[] });
  }

  if (e.eventType === "symptom") {
    const name = typeof p.name === "string" ? p.name : "";
    const canonicalKey = symptomFollowUpKey(name);
    return {
      key: `symptom::${canonicalKey}`,
      label: name || "Symptom",
      eventType: "symptom",
      fetchTypes: ["symptom"],
      matches: (ev) =>
        ev.eventType === "symptom" &&
        symptomFollowUpKey(
          typeof (ev.payload as Record<string, unknown>).name === "string"
            ? ((ev.payload as Record<string, unknown>).name as string)
            : ""
        ) === canonicalKey,
    };
  }

  if (e.eventType === "bowel_movement") {
    // A confirmed "didn't go" marker (CATCHUP_WIDGETS_PLAN.md D-CU-4) never counts
    // as a movement (D-CU-18): excluded from the item's calendar/count/series.
    return {
      key: "bowel_movement",
      label: eventTypeLabel("bowel_movement"),
      eventType: "bowel_movement",
      fetchTypes: ["bowel_movement"],
      matches: (ev) =>
        ev.eventType === "bowel_movement" &&
        (ev.payload as Record<string, unknown>).noMovement !== true,
    };
  }

  const type = e.eventType;
  return {
    key: type,
    label: eventTypeLabel(type),
    eventType: type,
    fetchTypes: [type],
    matches: (ev) => ev.eventType === type,
  };
}

const HEADACHE_KEY = symptomFollowUpKey("headache");

/**
 * D-COND-9: ItemIdentity for a Condition's tracked symptom, built directly
 * from a SymptomIdentity/TrackedSymptom key+label (no logged event needed).
 * Unlike itemForChartSeries's headache branch, when `key` is the canonical
 * headache key this merges BOTH `headache`-eventType events AND `symptom`-
 * eventType events resolving to that same key (D-COND-3) — matching
 * distinctSymptomIdentities' own grouping, which already folds those two
 * together into one identity. For any other key, only `symptom` events whose
 * name resolves to `key` match.
 */
export function itemForSymptomIdentity(key: string, label: string): ItemIdentity {
  const isHeadache = key === HEADACHE_KEY;
  return {
    key: isHeadache ? "headache" : `symptom::${key}`,
    label,
    eventType: isHeadache ? "headache" : "symptom",
    fetchTypes: ["symptom", "headache"],
    matches: (ev) => {
      if (ev.eventType === "headache") return isHeadache;
      if (ev.eventType === "symptom") {
        const name = (ev.payload as Record<string, unknown>).name;
        return symptomFollowUpKey(typeof name === "string" ? name : "") === key;
      }
      return false;
    },
  };
}

const MED_TYPES = new Set<EventType>(MED_EVENT_TYPES);

export function itemEvents(item: ItemIdentity, events: MedicalEvent[]): MedicalEvent[] {
  return events.filter((e) => {
    if (e.deleted) return false;
    if (MED_TYPES.has(e.eventType) && (e.payload as Record<string, unknown>).missed === true) return false;
    return item.matches(e);
  });
}

/**
 * D-IR-7: for symptom/headache items, mark which events start a new episode
 * so the Calendar/Count views count episodes rather than every follow-up.
 * `events` must already be item-scoped (itemEvents) and sorted ascending by
 * occurredAt. An event starts a new episode when it's the first event, the
 * previous event was a resolution (severity 0 or resolvedAt set), or the gap
 * since the previous event is >= autoCloseDays.
 */
export function episodeStartEvents(events: MedicalEvent[], autoCloseDays: number): Set<string> {
  const starts = new Set<string>();
  const autoCloseMs = autoCloseDays * 24 * 60 * 60 * 1000;
  let prev: MedicalEvent | null = null;
  for (const e of events) {
    if (!prev) {
      starts.add(e.eventId);
    } else {
      const prevPayload = prev.payload as Record<string, unknown>;
      const prevSeverity = typeof prevPayload.severity === "number" ? prevPayload.severity : undefined;
      const prevResolved = prevSeverity === 0 || typeof prevPayload.resolvedAt === "string";
      const gapMs = Date.parse(e.occurredAt) - Date.parse(prev.occurredAt);
      if (prevResolved || gapMs >= autoCloseMs) starts.add(e.eventId);
    }
    prev = e;
  }
  return starts;
}

const CHART_TYPE_REPORTABLE = new Set(["symptom", "vital", "sleep", "mood", "hydration"]);

/**
 * D-IR-11 adapter: build the ItemIdentity for a Charts-page series without
 * needing a logged event. Mirrors `itemFromEvent`'s scoping rules. Returns
 * null for chart types that aren't reportable (labs, body composition).
 */
export function itemForChartSeries(
  chartType: string,
  seriesKey: string,
  seriesName: string
): ItemIdentity | null {
  if (!CHART_TYPE_REPORTABLE.has(chartType)) return null;

  if (chartType === "symptom") {
    const headacheKey = symptomFollowUpKey("headache");
    if (seriesKey === headacheKey) {
      return {
        key: "headache",
        label: eventTypeLabel("headache"),
        eventType: "headache",
        fetchTypes: ["headache"],
        matches: (ev) => ev.eventType === "headache",
      };
    }
    return {
      key: `symptom::${seriesKey}`,
      label: seriesName,
      eventType: "symptom",
      fetchTypes: ["symptom"],
      matches: (ev) =>
        ev.eventType === "symptom" &&
        symptomFollowUpKey(
          typeof (ev.payload as Record<string, unknown>).name === "string"
            ? ((ev.payload as Record<string, unknown>).name as string)
            : ""
        ) === seriesKey,
    };
  }

  const type = ({ vital: "vital_sign", sleep: "sleep", mood: "mood", hydration: "hydration" } as const)[
    chartType as "vital" | "sleep" | "mood" | "hydration"
  ];
  return {
    key: type,
    label: eventTypeLabel(type),
    eventType: type,
    fetchTypes: [type],
    matches: (ev) => ev.eventType === type,
  };
}
